/**
 * Auto trendlines — Phase 2 of the chart pattern-recognition design
 * (docs/superpowers/specs/2026-07-16-chart-pattern-recognition-design.md §7).
 *
 * Pure functions on OHLCV bars, no klinecharts / React / DOM — the same seam as
 * `patternDetection.ts`, and the output is its frozen `Pattern` contract with
 * `type: 'trendline'`, so the overlay and a future Python engine both stay
 * agnostic.
 *
 * Algorithm: the brute-force shape TrendSpider documents. Take the ATR-scaled
 * ZigZag pivots, draw a candidate line through EVERY pair of same-kind pivots,
 * score each line, keep the few that matter. There is no curve fitting and no
 * merge step: a line is the two pivots it was drawn through, and the third,
 * fourth and fifth touches are COUNTED (every pivot within the touch tolerance
 * is a hit), not discovered. Pairs that count the same hits are one line and
 * collapse on that key. That keeps the whole thing O(m²) in the pivot count
 * with a single tight pass per pair, which is why it stays under 50 ms at the
 * pivot cap. (The earlier collinearity-walk-plus-merge design went cubic on
 * irregular intraday data and froze the tab for over a minute.)
 *
 * Units: x is the BAR INDEX (sessions have gaps, so a timestamp slope is
 * meaningless); y is the touch series price (wicks or candle bodies, see
 * `TouchInput`), and every tolerance is divided by the median ATR of the RAW
 * bars so the gates are instrument-independent in both touch modes. Swing
 * highs give resistance lines, swing lows give support lines. Horizontal lines
 * are deliberately excluded (`minRiseAtr`) — those are the S/R detector's job.
 */

import type { EdgeBar } from '../types/chart'
import { detectSwings, type Pattern, type Swing, type TouchInput } from './patternDetection'
import { trueRangeSeries, wilderSeries } from './seriesMath'

export type TrendlineKind = 'support' | 'resistance'

export interface TrendlineOptions {
  /** ATR period for the swing engine and the tolerance unit. Default 14. */
  atrPeriod?: number
  /** ZigZag reversal threshold, in ATR multiples. Default 3. */
  swingMult?: number
  /** Minimum hits (pivots on the line, the pair included). Default 2. */
  minTouches?: number
  /** Max |residual| / ATR for a pivot to count as a hit. Default 0.35. */
  touchTolAtr?: number
  /** Lines that rise or fall less than this many ATR across their span are
   *  horizontal and belong to S/R, not here. Default 1.0. */
  minRiseAtr?: number
  /** Minimum bars between the two pivots a line is drawn through. Default 10. */
  minSpanBars?: number
  /** Drop lines whose projection at the last bar is more than this many ATR
   *  from the last close. Default 6. */
  proximityAtr?: number
  /** Only the most recent bars are examined. The chart hands over its FULL
   *  loaded list (a 1m chart scrolled back can hold 20k bars) and detection
   *  re-runs on every bar close, so the work is bounded here rather than at
   *  the call site. A line older than this is off-screen anyway. Default 3000. */
  maxBars?: number
  /** Cap on pivots per kind fed to the pair walk, most recent kept. Default 150. */
  maxPivotsPerKind?: number
  /** Cap on unique lines per kind that get the (O(bars)) violation pass,
   *  best hits-then-fit first. Default 300. */
  maxScored?: number
  /** Wicks or candle bodies as the swing / touch series. Default 'wicks'. */
  touchInput?: TouchInput
}

export interface TrendlineCandidate {
  kind: TrendlineKind
  /** Bar indices of every pivot on the line (the hits), ascending. */
  barIdx: number[]
  /** Raw pivot prices, parallel to `barIdx`. */
  prices: number[]
  /** ATR magnitude of each pivot's swing (0 = unknown), parallel to `barIdx`. */
  magnitudes: number[]
  slope: number // price per bar
  intercept: number // price at bar index 0
  /** RMS residual of the hits against the line, in ATR units. */
  rmsResidual: number
  /** Bar indices of the two pivots the line was drawn through. */
  pair: [number, number]
}

const DEFAULTS: Required<TrendlineOptions> = {
  atrPeriod: 14,
  swingMult: 3,
  minTouches: 2,
  touchTolAtr: 0.35,
  minRiseAtr: 1.0,
  minSpanBars: 10,
  proximityAtr: 6,
  maxBars: 3000,
  maxPivotsPerKind: 150,
  maxScored: 300,
  touchInput: 'wicks',
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

/** Median ATR over the series — the scalar every tolerance is expressed in. The
 *  same fallback `detectSupportResistance` uses when ATR cannot warm up. */
export function medianAtr(bars: readonly EdgeBar[], atrPeriod = 14): number {
  if (bars.length === 0) return 0
  const vals = wilderSeries(trueRangeSeries(bars), atrPeriod).filter(
    (a): a is number => a != null && a > 0,
  )
  if (!vals.length) return bars[bars.length - 1].close * 0.01
  const s = [...vals].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/** The right edge every candidate is judged against by the proximity gate. */
export interface TrendlineHorizon {
  /** Index of the last bar (relative to the window the pivots came from). */
  lastIdx: number
  lastClose: number
}

/**
 * One candidate line per PAIR of same-kind pivots (time-ordered input), with
 * the cheap gates applied on the pair line first (span, rise, proximity when a
 * horizon is given), then the hits counted over every pivot of the kind. Pairs
 * that count the same hits are the same line: the one whose pair line fits
 * those hits best (lowest RMS) represents it.
 */
export function pairCandidates(
  pivots: readonly Swing[],
  kind: TrendlineKind,
  atr: number,
  opts: TrendlineOptions = {},
  horizon?: TrendlineHorizon,
): TrendlineCandidate[] {
  const o = { ...DEFAULTS, ...opts }
  const m = pivots.length
  if (!(atr > 0) || m < 2) return []
  const tol = o.touchTolAtr * atr
  const byHits = new Map<string, TrendlineCandidate>()
  for (let i = 0; i < m - 1; i++) {
    const a = pivots[i]
    for (let j = i + 1; j < m; j++) {
      const b = pivots[j]
      const span = b.index - a.index
      if (span < o.minSpanBars) continue
      const slope = (b.price - a.price) / span
      if ((Math.abs(slope) * span) / atr < o.minRiseAtr) continue
      const intercept = a.price - slope * a.index
      if (horizon) {
        const dist = Math.abs(slope * horizon.lastIdx + intercept - horizon.lastClose)
        if (dist > o.proximityAtr * atr) continue
      }
      // Hits: every pivot of this kind within the tolerance of the pair line.
      let hits = 0
      let ssr = 0
      let key = ''
      for (let k = 0; k < m; k++) {
        const p = pivots[k]
        const r = p.price - (slope * p.index + intercept)
        if (r <= tol && r >= -tol) {
          hits++
          ssr += r * r
          key += p.index + ','
        }
      }
      if (hits < o.minTouches) continue
      const rms = Math.sqrt(ssr / hits) / atr
      const prev = byHits.get(key)
      if (prev && prev.rmsResidual <= rms) continue
      const barIdx: number[] = []
      const prices: number[] = []
      const magnitudes: number[] = []
      for (let k = 0; k < m; k++) {
        const p = pivots[k]
        const r = p.price - (slope * p.index + intercept)
        if (r <= tol && r >= -tol) {
          barIdx.push(p.index)
          prices.push(p.price)
          magnitudes.push(p.magnitude)
        }
      }
      byHits.set(key, {
        kind,
        barIdx,
        prices,
        magnitudes,
        slope,
        intercept,
        rmsResidual: rms,
        pair: [a.index, b.index],
      })
    }
  }
  return [...byHits.values()]
}

/**
 * Decisive closes across a SLOPED level inside `[fromIdx, n-1]` — the same
 * ±0.5·ATR deadband rule as `levelCrossings`, with the level at `slope·i +
 * intercept` per bar. `lastSide` is the side of the last decisive close.
 */
export function lineCrossings(
  bars: readonly EdgeBar[],
  slope: number,
  intercept: number,
  fromIdx: number,
  atr: number,
): { crossings: number; lastSide: -1 | 0 | 1 } {
  const band = 0.5 * atr
  let side: -1 | 0 | 1 = 0
  let crossings = 0
  for (let i = Math.max(0, fromIdx); i < bars.length; i++) {
    const level = slope * i + intercept
    const c = bars[i].close
    const s: -1 | 0 | 1 = c > level + band ? 1 : c < level - band ? -1 : 0
    if (s === 0) continue
    if (side !== 0 && s !== side) crossings++
    side = s
  }
  return { crossings, lastSide: side }
}

/**
 * Full pipeline: bars → swings → pair candidates → score → `Pattern[]`.
 * Returns EVERY surviving line, unranked — `curateTrendlines` picks the few to draw.
 */
export function detectTrendlines(
  input: readonly EdgeBar[],
  symbol: string,
  timeframe: string,
  opts: TrendlineOptions = {},
): Pattern[] {
  const o = { ...DEFAULTS, ...opts }
  // Bounded work: the most recent window only (see `maxBars`). Indices below
  // are relative to this window; timestamps in the output are unaffected.
  const bars = input.length > o.maxBars ? input.slice(-o.maxBars) : input
  const n = bars.length
  if (n < 2) return []
  // ATR on the raw wicks regardless of the touch input (see the header).
  const atr = medianAtr(bars, o.atrPeriod)
  if (!(atr > 0)) return []
  const swings = detectSwings(bars, o.atrPeriod, o.swingMult, o.touchInput)
  if (swings.length < 2) return []
  const lastBar = bars[n - 1]
  const horizon: TrendlineHorizon = { lastIdx: n - 1, lastClose: lastBar.close }
  const out: Pattern[] = []

  for (const kind of ['support', 'resistance'] as const) {
    const all = swings.filter((s) => s.kind === (kind === 'support' ? 'low' : 'high'))
    const pivots = all.length > o.maxPivotsPerKind ? all.slice(-o.maxPivotsPerKind) : all
    const cands = pairCandidates(pivots, kind, atr, o, horizon)
      .sort((u, v) => v.barIdx.length - u.barIdx.length || u.rmsResidual - v.rmsResidual)
      .slice(0, o.maxScored)
    for (const c of cands) {
      const touches = c.barIdx.length
      const firstIdx = c.barIdx[0]
      const lastAnchorIdx = c.barIdx[touches - 1]
      const span = lastAnchorIdx - firstIdx
      const proj = (i: number) => c.slope * i + c.intercept
      const dist = Math.abs(proj(n - 1) - lastBar.close)

      const { crossings: violations, lastSide } = lineCrossings(
        bars,
        c.slope,
        c.intercept,
        firstIdx,
        atr,
      )
      const rising = c.slope > 0
      const direction: Pattern['direction'] = rising ? 'bullish' : 'bearish'
      const failed =
        (kind === 'support' && lastSide === -1) || (kind === 'resistance' && lastSide === 1)
      const status: Pattern['status'] = failed ? 'failed' : touches >= 3 ? 'confirmed' : 'forming'

      const mags = c.magnitudes.filter((m) => m > 0)
      const touchScore = clamp01((touches - 1) / 3)
      const spanScore = clamp01(span / (0.5 * n))
      const respect = clamp01(1 - violations / 3)
      const fit = clamp01(1 - c.rmsResidual / o.touchTolAtr)
      const recency = n > 1 ? clamp01(lastAnchorIdx / (n - 1)) : 1
      const prominence = mags.length
        ? clamp01(mags.reduce((s, m) => s + m, 0) / mags.length / 6)
        : 0
      const proximity = clamp01(1 - dist / (o.proximityAtr * atr))
      const quality =
        0.2 * touchScore +
        0.15 * spanScore +
        0.25 * respect +
        0.15 * fit +
        0.15 * recency +
        0.1 * prominence
      const relevance = 0.4 + 0.6 * proximity
      const confidence = clamp01(quality * relevance)

      out.push({
        id: `trendline-${kind}-${bars[firstIdx].timestamp}-${bars[lastAnchorIdx].timestamp}`,
        type: 'trendline',
        symbol,
        timeframe,
        anchors: c.barIdx.map((i, k) => ({
          timestamp: bars[i].timestamp,
          price: c.prices[k],
          role: 'touch',
        })),
        // The drawn segment is the pair line from the first hit to the last
        // bar; anchors keep every hit for explainability.
        lines: [
          {
            from: [bars[firstIdx].timestamp, proj(firstIdx)],
            to: [lastBar.timestamp, proj(n - 1)],
            kind,
          },
        ],
        direction,
        status,
        pivotPrice: proj(n - 1),
        confidence,
        scoreComponents: {
          touches: touchScore,
          span: spanScore,
          respect,
          fit,
          recency,
          prominence,
          proximity,
          touchCount: touches,
          violations,
          spanBars: span,
          slopeAtrPerBar: c.slope / atr,
          atr,
        },
        detectedAt: lastBar.timestamp,
      })
    }
  }
  return out
}

export interface TrendlineCurateOptions {
  /** Keep at most this many per direction (bullish / bearish). Default 3. */
  maxPerDirection?: number
  /** Drop lines with more decisive closes through them than this. Default 2. */
  maxViolations?: number
  /** Confidence floor. Default 0 — ChartManager curates at 0 and lets the
   *  slider only hide, the slider-decoupled shape S/R uses. */
  minConfidence?: number
  /** Near-duplicate slope tolerance, ATR per bar. Default 0.02. */
  slopeTolAtr?: number
  /** Near-duplicate right-edge offset tolerance, in ATR. Default 0.5. */
  offsetTolAtr?: number
}

/**
 * Display curation for trendlines. NOT `curatePatterns`: its non-maximum
 * suppression is a scalar price band on the mean anchor price, which would
 * suppress crossing lines of opposite slope. Duplicates here are lines sharing
 * 2+ anchors, or with the same kind, near-equal slope and near-equal value at
 * the right edge.
 */
export function curateTrendlines(
  patterns: readonly Pattern[],
  opts: TrendlineCurateOptions = {},
): Pattern[] {
  const {
    maxPerDirection = 3,
    maxViolations = 2,
    minConfidence = 0,
    slopeTolAtr = 0.02,
    offsetTolAtr = 0.5,
  } = opts
  const eligible = patterns
    .filter((p) => p.type === 'trendline')
    .filter((p) => (p.scoreComponents.violations ?? 0) <= maxViolations)
    .filter((p) => p.confidence >= minConfidence)
    .sort((a, b) => b.confidence - a.confidence)

  const kept: Pattern[] = []
  const perDir: Record<string, number> = {}
  const anchorsOf = (p: Pattern) => new Set(p.anchors.map((a) => a.timestamp))
  for (const p of eligible) {
    if ((perDir[p.direction] ?? 0) >= maxPerDirection) continue
    const mine = anchorsOf(p)
    const dupAt = kept.findIndex((k) => {
      if (k.lines[0]?.kind !== p.lines[0]?.kind) return false
      let shared = 0
      for (const a of k.anchors) if (mine.has(a.timestamp)) shared++
      if (shared >= 2) return true
      const atr = p.scoreComponents.atr || k.scoreComponents.atr || 0
      if (!(atr > 0)) return false
      const dSlope = Math.abs(
        (k.scoreComponents.slopeAtrPerBar ?? 0) - (p.scoreComponents.slopeAtrPerBar ?? 0),
      )
      const dOffset = Math.abs((k.lines[0]?.to[1] ?? 0) - (p.lines[0]?.to[1] ?? 0)) / atr
      return dSlope <= slopeTolAtr && dOffset <= offsetTolAtr
    })
    if (dupAt >= 0) {
      // A two-hit sub-segment of a longer line starts later, so it can out-score
      // the line it is part of on recency. The longer line is the one a trader
      // would draw: on a duplicate, the line with MORE anchors represents both.
      const k = kept[dupAt]
      if (p.anchors.length > k.anchors.length && p.direction === k.direction) kept[dupAt] = p
      continue
    }
    kept.push(p)
    perDir[p.direction] = (perDir[p.direction] ?? 0) + 1
  }
  return kept
}
